import { Divider, Typography } from '@mui/material';
import { Form } from 'formik';
import { assign, keys, pick } from 'lodash';
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import {
  CloseFormikDialogResult,
  DialogProps,
  FormControls,
  FormikCheckbox,
  FormikDialog,
  FormikNumericField,
  FormikSelect,
  FormikYesNoRadioGroup,
} from 'components';
import { useUserContextUtils } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { Bank, Currency, PaymentType, TradeMethodDto } from 'types';
import { formUtils } from 'utils';

type Values = TradeMethodDto;

type Props = {
  banks: Bank[];
  paymentTypes: PaymentType[];
  fiatCurrencies: Currency[];
} & DialogProps<CloseFormikDialogResult<Values>>;

export const TradeMethodDetailsDialog: React.FC<Props> = ({
  open,
  data,
  banks,
  paymentTypes,
  fiatCurrencies,
  onClose,
  ...rest
}) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.trade_methods',
  });
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);
  const { getPaymentTypesOptions } = useUserContextUtils();

  const isNew = useMemo(() => !data, [data]);

  const title = useMemo(
    () =>
      isNew ? t('details_dialog.create_title') : t('details_dialog.edit_title'),
    [t, isNew],
  );

  const getInitialValues = useCallback(
    () => ({
      bankId: '',
      fiatCurrencyId: '',
      paymentTypeId: '',
      enabled: true,
      useDistributionCache: true,
      parallelGroupOrdersEnabled: false,
      compareCardLast4DigitsEnabled: false,
      compareUTREnabled: false,
      compareAccountLast4DigitsEnabled: false,
      payinConsecutiveCancellations: 0,
      payinRequisiteConsecutiveCancellations: 0,
      payinRequisiteConsecutiveCancellationsTimeout: 0,
      payinRequisiteConsecutiveDisputes: 0,
      payinRequisiteConsecutiveManualComplete: 0,
      payoutRequisiteConsecutiveRejects: 0,
      payoutConsecutiveDisputes: 0,
      crossCurrencyDeviation: 0,
      payinStopScamReceiptsValidationEnabled: false,
      payoutStopScamReceiptsValidationEnabled: false,
      payinHoldCompletedTimeout: 0,
    }),
    [],
  );

  const [initialValues, setInitialValues] = useState(getInitialValues());

  const validationSchema: Yup.ObjectSchema<Values> = useMemo(
    () =>
      Yup.object().shape({
        bankId: Yup.string().required(tCommon('errors.required')),
        fiatCurrencyId: Yup.string().required(tCommon('errors.required')),
        paymentTypeId: Yup.string().required(tCommon('errors.required')),
        enabled: Yup.boolean().required(tCommon('errors.required')),
        useDistributionCache: Yup.boolean().required(
          tCommon('errors.required'),
        ),
        parallelGroupOrdersEnabled: Yup.boolean().required(
          tCommon('errors.required'),
        ),
        compareCardLast4DigitsEnabled: Yup.boolean().required(
          tCommon('errors.required'),
        ),
        compareAccountLast4DigitsEnabled: Yup.boolean().required(
          tCommon('errors.required'),
        ),
        compareUTREnabled: Yup.boolean().required(tCommon('errors.required')),
        payinConsecutiveCancellations: Yup.number().required(),
        payinRequisiteConsecutiveCancellations: Yup.number().required(),
        payinRequisiteConsecutiveCancellationsTimeout: Yup.number().required(),
        payinRequisiteConsecutiveDisputes: Yup.number().required(),
        payinRequisiteConsecutiveManualComplete: Yup.number().required(),
        payoutRequisiteConsecutiveRejects: Yup.number().required(),
        payoutConsecutiveDisputes: Yup.number().required(),
        crossCurrencyDeviation: Yup.number().required(),
        payinStopScamReceiptsValidationEnabled: Yup.boolean().required(
          tCommon('errors.required'),
        ),
        payoutStopScamReceiptsValidationEnabled: Yup.boolean().required(
          tCommon('errors.required'),
        ),
        payinHoldCompletedTimeout: Yup.number().required(),
      }),
    [tCommon],
  );

  useEffect(() => {
    const _initialValues = getInitialValues();
    setInitialValues(assign(_initialValues, pick(data, keys(_initialValues))));
  }, [data, getInitialValues]);

  const paymentTypesOptions = useMemo(
    () => getPaymentTypesOptions(paymentTypes),
    [paymentTypes, getPaymentTypesOptions],
  );

  return (
    <FormikDialog
      dialogProps={{
        open,
        title,
        onClose,
      }}
      formikProps={{
        initialValues,
        validateOnMount: true,
        validationSchema,
      }}
    >
      {() => (
        <Form>
          <FormControls>
            <FormikYesNoRadioGroup name="enabled" label={t('fields.enabled')} />
            <FormikSelect
              label={t('fields.payment_type')}
              name="paymentTypeId"
              options={paymentTypesOptions}
              disabled={!isNew}
              required
            />
            <FormikSelect
              label={t('fields.fiat_currency')}
              name="fiatCurrencyId"
              options={formUtils.getOptions(fiatCurrencies, 'code')}
              disabled={!isNew}
              required
            />
            <FormikSelect
              label={t('fields.bank')}
              name="bankId"
              options={formUtils.getOptions(banks)}
              disabled={!isNew}
              required
            />
            <Fragment>
              <Typography variant="h6">{tCommon('common.payin')}</Typography>
              <FormikYesNoRadioGroup
                name="payinStopScamReceiptsValidationEnabled"
                label={t('fields.stop_scam_receipts_validation_enabled')}
              />
              <FormikNumericField
                label={t('fields.payin_consecutive_cancellations')}
                name="payinConsecutiveCancellations"
                allowNegative={false}
                decimalScale={0}
                defaultValue={0}
              />
              <FormikNumericField
                label={t('fields.payin_requisite_consecutive_cancellations')}
                name="payinRequisiteConsecutiveCancellations"
                allowNegative={false}
                decimalScale={0}
                defaultValue={0}
              />
              <FormikNumericField
                label={t(
                  'fields.payin_requisite_consecutive_cancellations_timeout',
                )}
                name="payinRequisiteConsecutiveCancellationsTimeout"
                allowNegative={false}
                decimalScale={0}
                defaultValue={0}
              />
              <FormikNumericField
                label={t('fields.payin_requisite_consecutive_disputes')}
                name="payinRequisiteConsecutiveDisputes"
                allowNegative={false}
                decimalScale={0}
                defaultValue={0}
              />
              <FormikNumericField
                label={t('fields.payin_requisite_consecutive_manual_complete')}
                name="payinRequisiteConsecutiveManualComplete"
                allowNegative={false}
                decimalScale={0}
                defaultValue={0}
              />
              <FormikYesNoRadioGroup
                name="useDistributionCache"
                label={t('fields.use_distribution_cache')}
              />
              <FormikNumericField
                name="payinHoldCompletedTimeout"
                label={t('fields.payin_hold_completed_timeout')}
                allowNegative={false}
                decimalScale={0}
                defaultValue={0}
              />
              <Divider>{t('auto_close')}</Divider>
              <div>
                <FormikCheckbox
                  name="compareCardLast4DigitsEnabled"
                  label={t('fields.compare_card_last_4_digits_enabled')}
                />
                <FormikCheckbox
                  name="compareAccountLast4DigitsEnabled"
                  label={t('fields.compare_account_last_4_digits_enabled')}
                />
                <FormikCheckbox
                  name="compareUTREnabled"
                  label={t('fields.compare_utr_enabled')}
                />
                <FormikCheckbox
                  name="parallelGroupOrdersEnabled"
                  label={t('fields.parallel_group_orders_enabled')}
                />
              </div>
              <FormikNumericField
                name="crossCurrencyDeviation"
                label={t('fields.cross_currency_deviation')}
                required
                allowNegative={false}
                percentageSuffix
              />
            </Fragment>
            <Fragment>
              <Typography variant="h6">{tCommon('common.payout')}</Typography>
              <FormikYesNoRadioGroup
                name="payoutStopScamReceiptsValidationEnabled"
                label={t('fields.stop_scam_receipts_validation_enabled')}
              />
              <FormikNumericField
                label={t('fields.payout_consecutive_disputes')}
                name="payoutConsecutiveDisputes"
                allowNegative={false}
                decimalScale={0}
                defaultValue={0}
              />
              <FormikNumericField
                label={t('fields.payout_requisite_consecutive_rejects')}
                name="payoutRequisiteConsecutiveRejects"
                allowNegative={false}
                decimalScale={0}
                defaultValue={0}
              />
            </Fragment>
          </FormControls>
        </Form>
      )}
    </FormikDialog>
  );
};
